import { useState, useCallback, useEffect } from "react";
import { AxiosError } from "axios";
import axiosInstance from "../../../utility/axiosInstance";
import { PretestAss } from "../../../store/types/job";

interface UseFetchDataResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  fetchData: () => Promise<void>;
  fetchData2?: () => Promise<void>;
  refetch: () => Promise<void>;
  refetch2: () => Promise<void>;
}

export function useFetchData<T>(url: string): UseFetchDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get<T>(url);
      setData(response.data);
    } catch (err) {
      const error =
        err instanceof AxiosError
          ? new Error(err.response?.data?.message || err.message)
          : err instanceof Error
          ? err
          : new Error("An unexpected error occurred");

      setError(error);
      console.error(`Error fetching data from ${url}:`, err);
    } finally {
      setLoading(false);
    }
  }, [url]);

  const fetchData2 = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get<T>(url);
      setData(response.data);
    } catch (err) {
      const error =
        err instanceof AxiosError
          ? new Error(err.response?.data?.message || err.message)
          : err instanceof Error
          ? err
          : new Error("An unexpected error occurred");

      setError(error);
      console.error(`Error fetching data from ${url}:`, err);
    } finally {
      setLoading(false);
    }
  }, [url]);

  return {
    data,
    loading,
    error,
    fetchData,
    fetchData2,
    refetch: fetchData,
    refetch2: fetchData2,
  };
}

interface iPretestFetch {
  id: string;
  currentTemplate: {
    title: string;
  };
  // Add other job properties as needed
}

interface GetPretestsReturn {
  pretests?: PretestAss[];
  assessments?: PretestAss[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const getPretests = (): GetPretestsReturn => {
  const [pretests, setPretests] = useState<PretestAss[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPretests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get<{
        message: string;
        data: PretestAss[];
      }>("/admin/organization/pretests");
      setPretests(response.data.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch pretests";
      setError(errorMessage);
      console.error("Error fetching pretests:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPretests();
  }, []);

  return { pretests, loading, error, refetch: fetchPretests };
};

export const getAssessments = (): GetPretestsReturn => {
  const [assessments, setPretests] = useState<PretestAss[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get<{
        message: string;
        data: PretestAss[];
      }>("/admin/organization/assessments");
      setPretests(response.data.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch assessments";
      setError(errorMessage);
      console.error("Error fetching assessments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  return { assessments, loading, error, refetch: fetchAssessments };
};
